/**
 * Weather Service for Stand Up Paddle (SUP) App
 * Fetches real-time weather and marine data from Open-Meteo API (Free, No API Key)
 * CRITICAL: Real data is essential for SUP safety
 */

const OPEN_METEO_API = 'https://api.open-meteo.com/v1/forecast';

/**
 * Fetch real-time weather and marine data for a specific location
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object|null>} Weather data object or null on error
 */
export const getSpotWeather = async (lat, lng) => {
  try {
    // Validate inputs
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
      console.error('Invalid coordinates provided to weatherService');
      return null;
    }

    // Build API URL with required parameters
    const params = new URLSearchParams({
      latitude: lat,
      longitude: lng,
      current: 'temperature_2m,wind_speed_10m,wind_direction_10m',
      hourly: 'wave_height,temperature_2m',
      timezone: 'auto'
    });

    const url = `${OPEN_METEO_API}?${params?.toString()}`;

    // Fetch data from Open-Meteo
    const response = await fetch(url);

    if (!response?.ok) {
      throw new Error(`Open-Meteo API error: ${response.status}`);
    }

    const data = await response?.json();

    // Extract current weather data
    const current = data?.current;
    const hourly = data?.hourly;

    if (!current) {
      console.error('No current weather data in API response');
      return null;
    }

    // Get current wave height from hourly data (first value is current hour)
    const currentWaveHeight = hourly?.wave_height?.[0] || 0;

    // Return clean, structured object
    return {
      temp: Math.round(current?.temperature_2m || 0),
      windSpeed: Math.round(current?.wind_speed_10m || 0),
      windDirection: current?.wind_direction_10m || 0,
      waveHeight: parseFloat(currentWaveHeight?.toFixed(2))
    };
  } catch (error) {
    console.warn('Weather data unavailable:', error);
    // Return null on error - UI will handle gracefully
    return null;
  }
};

/**
 * Calculate SUP suitability based on weather conditions
 * @param {Object} weather - Weather data object
 * @returns {Object} Suitability badge info { level, text, color }
 */
export const getSUPSuitability = (weather) => {
  if (!weather) {
    return {
      level: 'unknown',
      text: 'Veri Yok',
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    };
  }

  const { windSpeed, waveHeight } = weather;

  // Perfect conditions: Low wind and calm waves
  if (windSpeed < 12 && waveHeight < 0.3) {
    return {
      level: 'perfect',
      text: 'Mükemmel Koşullar',
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    };
  }

  // Dangerous conditions: High wind or big waves
  if (windSpeed > 20 || waveHeight > 0.8) {
    return {
      level: 'dangerous',
      text: 'Zorlu/Tehlikeli',
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
  }

  // Intermediate conditions
  return {
    level: 'intermediate',
    text: 'Orta Seviye',
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
  };
};

/**
 * Fetch weather forecast for a specific event date AND time
 * Returns forecast data if event is within 5 days, otherwise returns null
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {string} eventDate - Event date in ISO format (YYYY-MM-DD or full ISO)
 * @param {string} [eventTime] - Event time in HH:MM format (optional, e.g. "09:00")
 * @returns {Promise<Object|null>} Forecast data or null if > 5 days away
 */
export const getForecastForEventDate = async (lat, lng, eventDate, eventTime = null) => {
  try {
    // Validate inputs
    if (!lat || !lng || !eventDate || isNaN(lat) || isNaN(lng)) {
      console.error('Invalid parameters provided to getForecastForEventDate');
      return null;
    }

    // Parse event date - handle multiple formats
    let targetDate;
    
    // If eventDate is already a Date object
    if (eventDate instanceof Date) {
      targetDate = new Date(eventDate);
    } 
    // If it's a string in ISO format (YYYY-MM-DD or full ISO)
    else if (typeof eventDate === 'string') {
      // Try parsing as ISO first
      targetDate = new Date(eventDate);
      
      // If invalid, try to extract date from formatted string
      if (isNaN(targetDate?.getTime())) {
        // Try to match YYYY-MM-DD pattern in the string
        const isoMatch = eventDate?.match(/\d{4}-\d{2}-\d{2}/);
        if (isoMatch) {
          targetDate = new Date(isoMatch[0]);
        } else {
          console.warn('Unable to parse event date:', eventDate);
          return null;
        }
      }
    } else {
      console.warn('Invalid event date format:', eventDate);
      return null;
    }

    // Validate parsed date
    if (isNaN(targetDate?.getTime())) {
      console.warn('Invalid date after parsing:', eventDate);
      return null;
    }

    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const targetDateOnly = new Date(targetDate);
    targetDateOnly?.setHours(0, 0, 0, 0);

    // Calculate days difference
    const diffTime = targetDateOnly - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Return null if event is more than 5 days away
    if (diffDays > 5) {
      return { tooFarAway: true, daysUntilEvent: diffDays };
    }

    // Return null if event is in the past
    if (diffDays < 0) {
      return { isPast: true };
    }

    const targetDateStr = targetDateOnly?.toISOString()?.split('T')?.[0];

    // Parse event hour for hourly lookup (default to 10:00 if not provided)
    let eventHour = 10;
    if (eventTime && typeof eventTime === 'string') {
      const timeParts = eventTime?.match(/^(\d{1,2}):(\d{2})/);
      if (timeParts) {
        eventHour = parseInt(timeParts?.[1], 10);
      }
    }

    // Build API URL with HOURLY data for time-specific forecast
    const params = new URLSearchParams({
      latitude: lat,
      longitude: lng,
      hourly: 'temperature_2m,windspeed_10m,winddirection_10m,weathercode,wave_height',
      daily: 'temperature_2m_max,temperature_2m_min,weathercode,windspeed_10m_max,winddirection_10m_dominant',
      timezone: 'auto',
      forecast_days: 7
    });

    const url = `${OPEN_METEO_API}?${params?.toString()}`;

    // Fetch forecast data
    const response = await fetch(url);

    if (!response?.ok) {
      throw new Error(`Open-Meteo API error: ${response.status}`);
    }

    const data = await response?.json();
    const hourly = data?.hourly;
    const daily = data?.daily;

    // --- Hourly lookup for event time ---
    let hourlyData = null;
    if (hourly?.time) {
      // Find the hourly index matching event date + event hour
      const targetHourStr = `${targetDateStr}T${String(eventHour)?.padStart(2, '0')}:00`;
      let hourIndex = hourly?.time?.findIndex(t => t === targetHourStr);

      // If exact hour not found, find closest hour on that day
      if (hourIndex === -1) {
        hourIndex = hourly?.time?.findIndex(t => t?.startsWith(targetDateStr));
        // Find the closest hour to eventHour on that day
        const dayHours = hourly?.time
          ?.map((t, i) => ({ t, i }))
          ?.filter(({ t }) => t?.startsWith(targetDateStr));
        
        if (dayHours?.length > 0) {
          const closest = dayHours?.reduce((prev, curr) => {
            const prevHour = parseInt(prev?.t?.split('T')?.[1]?.split(':')?.[0], 10);
            const currHour = parseInt(curr?.t?.split('T')?.[1]?.split(':')?.[0], 10);
            return Math.abs(currHour - eventHour) < Math.abs(prevHour - eventHour) ? curr : prev;
          });
          hourIndex = closest?.i;
        }
      }

      if (hourIndex !== -1 && hourIndex !== undefined) {
        hourlyData = {
          temperature: Math.round(hourly?.temperature_2m?.[hourIndex] || 0),
          windSpeed: Math.round(hourly?.windspeed_10m?.[hourIndex] || 0),
          windDirection: hourly?.winddirection_10m?.[hourIndex] || 0,
          weatherCode: hourly?.weathercode?.[hourIndex] || 0,
          waveHeight: parseFloat((hourly?.wave_height?.[hourIndex] || 0)?.toFixed(2)),
          hour: eventHour
        };
      }
    }

    // --- Daily fallback ---
    if (!daily || !daily?.time) {
      console.error('No daily forecast data in API response');
      return null;
    }

    let dateIndex = daily?.time?.findIndex(date => date === targetDateStr);

    if (dateIndex === -1) {
      dateIndex = daily?.time?.findIndex(date => {
        const apiDate = new Date(date);
        return apiDate?.getFullYear() === targetDateOnly?.getFullYear() &&
               apiDate?.getMonth() === targetDateOnly?.getMonth() &&
               apiDate?.getDate() === targetDateOnly?.getDate();
      });
    }

    if (dateIndex === -1 && daily?.time?.length > 0) {
      const targetTime = targetDateOnly?.getTime();
      let closestIndex = 0;
      let closestDiff = Math.abs(new Date(daily.time[0])?.getTime() - targetTime);
      
      for (let i = 1; i < daily?.time?.length; i++) {
        const diff = Math.abs(new Date(daily.time[i])?.getTime() - targetTime);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = i;
        }
      }
      
      if (closestDiff <= 86400000) {
        dateIndex = closestIndex;
      }
    }

    if (dateIndex === -1) {
      return null;
    }

    const dailyWeatherCode = daily?.weathercode?.[dateIndex] || 0;

    // Use hourly data for the event time if available, otherwise fall back to daily
    const finalWindSpeed = hourlyData ? hourlyData?.windSpeed : Math.round(daily?.windspeed_10m_max?.[dateIndex] || 0);
    const finalWindDirection = hourlyData ? hourlyData?.windDirection : (daily?.winddirection_10m_dominant?.[dateIndex] || 0);
    const finalWeatherCode = hourlyData ? hourlyData?.weatherCode : dailyWeatherCode;
    const finalTemperature = hourlyData
      ? { max: hourlyData?.temperature, min: hourlyData?.temperature, atEventTime: hourlyData?.temperature }
      : {
          max: Math.round(daily?.temperature_2m_max?.[dateIndex] || 0),
          min: Math.round(daily?.temperature_2m_min?.[dateIndex] || 0)
        };

    return {
      date: targetDateStr,
      daysUntilEvent: diffDays,
      eventHour: hourlyData ? eventHour : null,
      isHourlyData: !!hourlyData,
      waveHeight: hourlyData?.waveHeight ?? null,
      temperature: finalTemperature,
      windSpeed: finalWindSpeed,
      windDirection: finalWindDirection,
      condition: getWeatherCondition(finalWeatherCode),
      weatherCode: finalWeatherCode
    };
  } catch (error) {
    console.warn('Forecast data unavailable:', error);
    return null;
  }
};

/**
 * Convert WMO weather code to human-readable condition
 * @param {number} code - WMO weather code
 * @returns {Object} Weather condition with icon and description
 */
const getWeatherCondition = (code) => {
  // WMO Weather interpretation codes
  const conditions = {
    0: { icon: 'Sun', description: 'Açık', iconName: 'Sun' },
    1: { icon: 'Sun', description: 'Çoğunlukla Açık', iconName: 'Sun' },
    2: { icon: 'Cloud', description: 'Parçalı Bulutlu', iconName: 'Cloud' },
    3: { icon: 'Cloud', description: 'Bulutlu', iconName: 'Cloud' },
    45: { icon: 'CloudFog', description: 'Sisli', iconName: 'CloudFog' },
    48: { icon: 'CloudFog', description: 'Dondurucu Sis', iconName: 'CloudFog' },
    51: { icon: 'CloudDrizzle', description: 'Hafif Çisenti', iconName: 'CloudDrizzle' },
    53: { icon: 'CloudDrizzle', description: 'Çisenti', iconName: 'CloudDrizzle' },
    55: { icon: 'CloudDrizzle', description: 'Yoğun Çisenti', iconName: 'CloudDrizzle' },
    61: { icon: 'CloudRain', description: 'Hafif Yağmur', iconName: 'CloudRain' },
    63: { icon: 'CloudRain', description: 'Yağmur', iconName: 'CloudRain' },
    65: { icon: 'CloudRain', description: 'Şiddetli Yağmur', iconName: 'CloudRain' },
    71: { icon: 'Snowflake', description: 'Hafif Kar', iconName: 'Snowflake' },
    73: { icon: 'Snowflake', description: 'Kar', iconName: 'Snowflake' },
    75: { icon: 'Snowflake', description: 'Yoğun Kar', iconName: 'Snowflake' },
    77: { icon: 'Snowflake', description: 'Kar Taneleri', iconName: 'Snowflake' },
    80: { icon: 'CloudRain', description: 'Sağanak Yağmur', iconName: 'CloudRain' },
    81: { icon: 'CloudRain', description: 'Orta Sağanak', iconName: 'CloudRain' },
    82: { icon: 'CloudRain', description: 'Şiddetli Sağanak', iconName: 'CloudRain' },
    85: { icon: 'Snowflake', description: 'Kar Yağışı', iconName: 'Snowflake' },
    86: { icon: 'Snowflake', description: 'Yoğun Kar Yağışı', iconName: 'Snowflake' },
    95: { icon: 'CloudLightning', description: 'Gök Gürültülü Fırtına', iconName: 'CloudLightning' },
    96: { icon: 'CloudLightning', description: 'Dolu ile Fırtına', iconName: 'CloudLightning' },
    99: { icon: 'CloudLightning', description: 'Şiddetli Dolu Fırtınası', iconName: 'CloudLightning' }
  };

  return conditions?.[code] || { icon: 'Cloud', description: 'Bilinmeyen', iconName: 'Cloud' };
};

export const weatherService = {
  getSpotWeather,
  getSUPSuitability,
  getForecastForEventDate
};